import React from 'react';
import styles from './Cart.scss';
import classNames from 'classnames';
import CurrencyUtils from '@wix/wixrest-utils/dist/CurrencyUtils';
import DiscountInput from '../DiscountInput';
import { func, number, object, string, oneOf } from 'prop-types';
import { translate } from 'react-i18next';
import { CurrencyInput } from '../PaymentLine/CurrencyInput';
import { DeliveryTypes } from '../../utils/constants';
import { Text, Heading, Input } from 'wix-style-react';

function CartSummary({
  priceComponents,
  currency,
  locale,
  acceptsTip,
  t,
  onChangeDeliveryFee,
  allowsManagerDiscount,
  deliveryType,
}) {
  const subtotal = CurrencyUtils.toStr((priceComponents.subtotal || 0) / 100, currency, locale);
  const tax = CurrencyUtils.toStr((priceComponents.tax || 0) / 100, currency, locale);
  const tip = CurrencyUtils.toStr((priceComponents.tip || 0) / 100, currency, locale);
  const total = CurrencyUtils.toStr((priceComponents.total || 0) / 100, currency, locale);
  const currencySymbol = CurrencyUtils.currencySymbol(currency);
  const deliveryFee = priceComponents.deliveryFee;

  const prefix = <Input.Affix>{currencySymbol}</Input.Affix>;

  return (
    <div className={styles.pricing} data-hook="cart-summary-wrapper">
      <div className={classNames([styles.pricingRow, styles.extraPadding])} data-hook="cart-summary-subtotal-wrapper">
        <Text size="medium" weight="normal" data-hook="cart-subtotal-title">
          {t('phoneordering_checkout_subtotal_label')}
        </Text>
        <Text size="medium" weight="normal" data-hook="cart-subtotal-value">
          {subtotal}
        </Text>
      </div>
      <div className={classNames([styles.pricingRow, styles.extraPadding])} data-hook="cart-summary-taxes-wrapper">
        <Text size="medium" weight="normal" data-hook="cart-tax-title">
          {t('phoneordering_checkout_tax_label')}
        </Text>
        <Text size="medium" weight="normal" data-hook="cart-tax-value">
          {tax}
        </Text>
      </div>
      {deliveryType === DeliveryTypes.DELIVERY && (
        <div className={`${styles.pricingRow} ${styles.pricingRowInput}`} data-hook="cart-summary-delivery-wrapper">
          <Text size="medium" weight="normal" data-hook="cart-delivery-title">
            {t('phoneordering_checkout_fee_label')}
          </Text>
          <div className={styles.pricingInput}>
            <CurrencyInput
              locale={locale}
              currency={currency}
              value={deliveryFee}
              onChange={onChangeDeliveryFee}
              prefix={prefix}
              dataHook="cart-delivery-input"
            />
          </div>
        </div>
      )}
      {allowsManagerDiscount && (
        <div className={`${styles.pricingRow} ${styles.pricingRowInput}`} data-hook="cart-summary-discount-wrapper">
          <Text size="medium" weight="normal" data-hook="cart-discount-title">
            {t('phoneordering_checkout_discount_label')}
          </Text>
          <div className={styles.pricingInput} data-hook="cart-discount-value">
            <DiscountInput />
          </div>
        </div>
      )}
      {acceptsTip && (
        <div className={classNames([styles.pricingRow, styles.extraPadding])} data-hook="cart-summary-tip-wrapper">
          <Text size="medium" weight="normal" data-hook="cart-tip-title">
            Tip
          </Text>
          <Text size="medium" weight="normal" data-hook="cart-tip-value">
            {tip}
          </Text>
        </div>
      )}
      <div className={classNames([styles.pricingRow, styles.extraPadding])} data-hook="cart-summary-total-wrapper">
        <Heading appearance="H3" dataHook="cart-total-title">
          {t('phoneordering_checkout_total_label')}
        </Heading>
        <Heading appearance="H3" dataHook="cart-total-value">
          {total}
        </Heading>
      </div>
    </div>
  );
}

CartSummary.displayName = 'CartSummary';
CartSummary.propTypes = {
  priceComponents: object,
  currencySymbol: string,
  currency: string,
  locale: string,
  onManagerDiscountChange: func,
  discountInputValue: number,
  t: func,
  deliveryType: oneOf([DeliveryTypes.DELIVERY, DeliveryTypes.TAKEOUT]),
};

export default translate()(CartSummary);
